import { useSetAtom } from 'jotai';
import { useEffect } from 'react';

import { addInteractionEvent } from '@creditornot/browser-telemetry';
import { NavigationBar } from 'common/components/cookbook/NavigationBar/NavigationBar';
import { PageSpinner } from 'common/components/cookbook/PageSpinner';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { usePageView } from 'common/hooks/usePageView';
import { ItemChangeLogLegacy } from 'features/warehouse/common/components/ItemChangeLogLegacy/ItemChangeLogLegacy';
import { ItemInformation } from 'features/warehouse/common/components/ItemInformation/ItemInformation';
import { WarehousePageApiError } from 'features/warehouse/common/components/WarehouseApiErrorNotification';
import { useWoltItemQuery } from 'features/warehouse/common/query';

import { AddToInventoryForm } from './AddToInventoryForm/AddToInventoryForm';
import { ShipmentInformation } from './ShipmentInformation';
import { addToInventoryRoute } from '../index';
import { useInboundLoadQuery } from '../query';
import { automaticNavigationDisabledAtom } from '../state';
import { getExpectedQuantity } from '../utils';

type Props = {
  selectedWarehouseId: string;
  selectedItemId: string;
  handleClose: (preserveSearchQuery?: boolean) => void;
};

export const AddItemToInventory = ({ selectedWarehouseId, selectedItemId, handleClose }: Props) => {
  const { inboundLoadId } = addToInventoryRoute.useSearch();
  const setIsAutomaticNavigationDisabled = useSetAtom(automaticNavigationDisabledAtom);
  const f = useFormatMessage();

  const {
    data: itemData,
    isLoading: isLoadingItemData,
    error: errorLoadingItemData,
    isFetching: isFetchIngItemData,
  } = useWoltItemQuery({ warehouseId: selectedWarehouseId, itemId: selectedItemId });

  const {
    data: inboundLoadData,
    isLoading: isLoadingInboundLoad,
    error: errorLoadingInboundLoad,
    isFetching: isFetchingInboundLoad,
  } = useInboundLoadQuery(selectedWarehouseId, inboundLoadId, {
    enabled: Boolean(inboundLoadId && selectedWarehouseId),
  });
  const inboundLoadItem = inboundLoadData?.items.find(
    (inboundLoadItem) => inboundLoadItem.itemId === selectedItemId,
  );

  const expectedQuantity =
    inboundLoadItem?.orderedQuantity.measurementUnit === 'UNITS'
      ? getExpectedQuantity(
          inboundLoadItem.orderedQuantity.value,
          inboundLoadItem.receivedQuantity.value,
        )
      : undefined;

  useEffect(() => {
    if (selectedItemId) {
      setIsAutomaticNavigationDisabled(true);
    }
  }, [selectedItemId, setIsAutomaticNavigationDisabled]);

  usePageView('add_to_inventory', {
    wm_venue_id: selectedWarehouseId,
    gtin: itemData?.gtin,
    item_id: itemData?.id,
  });

  const isActiveLoadingItemData = isLoadingItemData && isFetchIngItemData;
  const isActiveLoadingInboundLoadData = isLoadingInboundLoad && isFetchingInboundLoad;
  const isLoading = isActiveLoadingItemData || isActiveLoadingInboundLoadData;

  const loadingError = errorLoadingItemData || errorLoadingInboundLoad;

  return (
    <>
      <NavigationBar
        title={f('receiving.add_to_inventory')}
        onBackClick={() => {
          handleClose(true);
          addInteractionEvent('add_to_inventory', 'click', {
            item_id: itemData?.id,
            click_target: 'back',
            gtin: itemData?.gtin,
            wm_venue_id: selectedWarehouseId,
          });
        }}
      />
      <WarehousePageApiError error={loadingError} />
      {isLoading && <PageSpinner />}
      {itemData && (
        <>
          <ItemInformation item={itemData} view="add_to_inventory" />
          {inboundLoadData && (
            <ShipmentInformation
              inboundLoadId={inboundLoadData.id}
              shipmentName={inboundLoadData.publicId}
              expectedQuantity={expectedQuantity}
            />
          )}

          <AddToInventoryForm
            selectedWarehouseId={selectedWarehouseId}
            selectedItem={itemData}
            inboundLoadId={inboundLoadData?.id}
            poPublicId={inboundLoadData?.publicId}
            expectedQuantity={expectedQuantity}
            onClose={handleClose}
          />
          <ItemChangeLogLegacy
            selectedWarehouseId={selectedWarehouseId}
            selectedItemId={itemData.id}
          />
        </>
      )}
    </>
  );
};
